import Image from 'next/legacy/image';
import {Button, Box, Heading, Center, Grid, useDisclosure} from '@chakra-ui/react';
import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';

import {useGetMeQueryData} from '@eksab/hooks';
import {Card} from '@eksab/components';
import {useTranslate} from '@eksab/i18n';

import {CoinsIcon} from '@eksab/assets/icons';
import {AuthModal} from '@eksab/features/auth/modals/AuthModal';

import type {StoreProduct} from '../types';

type Props = {
  product: StoreProduct;
};

export const Product = ({product: {name, price, image, id}}: Props) => {
  const formatNumber = useIntl().formatNumber;
  const router = useRouter();
  const authDisclosure = useDisclosure();

  const t = useTranslate();
  const isUser = useGetMeQueryData();

  const onProductOpen = () => {
    if (!isUser()) return authDisclosure.onOpen();

    router.push(`/store/market/${id}`);
  };

  const onAuthModalClose = () => {
    authDisclosure.onClose();

    if (!isUser()) return;

    router.push(`/store/market/${id}`);
  };

  return (
    <Card
      onClick={onProductOpen}
      display="flex"
      flexDir="column"
      cursor="pointer"
      justifyContent="space-between"
      gap="1rem"
      p="6"
      pt="5"
      _hover={{'.img': {transform: 'scale(1.1)'}}}
    >
      <Grid
        flex="1"
        templateColumns={{base: '2fr 1fr', sm: '1fr'}}
        templateRows={{base: '1fr 1fr', sm: 'auto 1fr 40px'}}
        justifyItems={['start', 'center']}
        gap="2"
      >
        <Heading
          noOfLines={[2, 1, null, 2]}
          fontWeight="700"
          textAlign={['start', 'center']}
          fontSize="clamp(1rem, 1.46vw, 1.375rem)"
          lineHeight="7"
        >
          {name}
        </Heading>

        <Center
          className="img"
          alignSelf="center"
          gridArea={['1/2/3/2', 'initial']}
          transition="transform 0.4s ease-out"
        >
          {!!(image?.thumb ?? image?.original) && (
            <Image src={image.thumb ?? image.original} alt={name} width="150" height="120" objectFit="contain" />
          )}
        </Center>

        <Box
          alignSelf="end"
          display="flex"
          alignItems="center"
          gap="0.625rem"
          fontSize="clamp(1.875rem, 2.5vw, 2.25rem)"
        >
          <CoinsIcon width="1.2em" height="1.2em" />
          <Heading fontSize="inherit">{formatNumber(price)}</Heading>
        </Box>
      </Grid>

      <Button w="full">{t('app.actions.redeem')}</Button>
      <AuthModal {...authDisclosure} onClose={onAuthModalClose} />
    </Card>
  );
};
